import * as THREE from 'three';
import { damp } from '../util/math';

export const BALL_RADIUS = 0.7;

export type Expression = 'neutral' | 'content' | 'party' | 'scream' | 'dazed';

/**
 * The panda ball (§9 feel): a chubby panda curled into a roll. The body
 * (`shell`) — belly, tucked arms & feet, ears, and face — tumbles with the
 * physics ball so you see it roll. Squash & stretch live on the non-rotating
 * `root`.
 */
export class Panda {
  readonly root = new THREE.Group();
  private shell = new THREE.Group();
  private face = new THREE.Group();
  private pupilL: THREE.Mesh;
  private pupilR: THREE.Mesh;
  private eyeL: THREE.Mesh;
  private eyeR: THREE.Mesh;
  private mouth: THREE.Mesh;
  private frown: THREE.Mesh;
  private partyHat = new THREE.Group();
  private basePupilX = BALL_RADIUS * 0.33;

  private squash = new THREE.Vector3(1, 1, 1);
  private squashVel = new THREE.Vector3(0, 0, 0);
  private dazeTime = 0;
  private expression: Expression = 'neutral';
  // Rolls the model 180° so the face leads in the travel direction (the ball
  // rolls toward -Z). Applied only to the in-game rotation; the menu viewer
  // drives root.rotation directly and keeps the face toward the camera.
  private faceForward = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), Math.PI);

  constructor() {
    this.root.add(this.shell);

    const R = BALL_RADIUS;
    const white = new THREE.MeshStandardMaterial({ color: 0xf3f3f0, roughness: 0.75 });
    const black = new THREE.MeshStandardMaterial({ color: 0x1e1e1e, roughness: 0.65 });
    const pad = new THREE.MeshStandardMaterial({ color: 0x3a342f, roughness: 0.7 });

    // Reused unit sphere, scaled per "blob" — chunky panda parts are all ovoids.
    const unit = new THREE.SphereGeometry(1, 16, 12);
    const blob = (
      parent: THREE.Object3D,
      mat: THREE.Material,
      x: number,
      y: number,
      z: number,
      sx: number,
      sy: number,
      sz: number,
    ): THREE.Mesh => {
      const m = new THREE.Mesh(unit, mat);
      m.position.set(x, y, z);
      m.scale.set(sx, sy, sz);
      m.castShadow = true;
      parent.add(m);
      return m;
    };

    // Big round belly (the main rolling mass; its bottom sits at -R for ground
    // contact). The collider stays a sphere of radius R regardless.
    const body = new THREE.Mesh(new THREE.SphereGeometry(R * 0.95, 28, 20), white);
    body.position.set(0, -R * 0.05, 0);
    body.castShadow = true;
    this.shell.add(body);

    // A distinct, smaller head clearly on top & forward, in its own group so we
    // can push it forward and tilt it up a hair — so the whole face (incl. the
    // mouth) reads as being ON the head, not the belly. Everything below is
    // relative to the head's own centre. The collider stays a sphere of R.
    const head = new THREE.Group();
    head.position.set(0, R * 0.64, R * 0.5);
    head.rotation.x = -0.22; // clear chin-up tilt so the head/face pop out
    this.shell.add(head);
    head.add(this.face);

    const headSphere = new THREE.Mesh(new THREE.SphereGeometry(R * 0.52, 24, 18), white);
    headSphere.castShadow = true;
    head.add(headSphere);

    // Chunky black ears with a darker inner — on top of the head.
    for (const sx of [-1, 1]) {
      blob(head, black, sx * R * 0.34, R * 0.4, -R * 0.06, R * 0.25, R * 0.25, R * 0.21);
      blob(head, pad, sx * R * 0.34, R * 0.4, R * 0.08, R * 0.13, R * 0.13, R * 0.08);
    }

    // Party hat (shown only when the panda is "over the moon").
    const coneGeo = new THREE.ConeGeometry(R * 0.3, R * 0.62, 18);
    const cone = new THREE.Mesh(
      coneGeo,
      new THREE.MeshStandardMaterial({ color: 0xff5da2, roughness: 0.5, emissive: 0x3a0d22 }),
    );
    cone.position.set(0, R * 0.58, 0);
    // Candy stripes around the cone.
    for (let i = 0; i < 3; i++) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(R * (0.22 - i * 0.06), R * 0.03, 8, 18),
        new THREE.MeshStandardMaterial({ color: 0xffe14d, roughness: 0.4 }),
      );
      ring.rotation.x = Math.PI / 2;
      ring.position.set(0, R * (0.38 + i * 0.2), 0);
      this.partyHat.add(ring);
    }
    const pom = new THREE.Mesh(
      new THREE.SphereGeometry(R * 0.09, 12, 10),
      new THREE.MeshStandardMaterial({ color: 0x6df0ff, roughness: 0.3, emissive: 0x0a3a44 }),
    );
    pom.position.set(0, R * 0.9, 0);
    this.partyHat.add(cone, pom);
    this.partyHat.visible = false;
    head.add(this.partyHat);

    // Four limbs on the belly — front paws (hands) high on the front-sides,
    // hind feet low at the bottom, kept well apart so arms & legs don't merge.
    for (const sx of [-1, 1]) {
      blob(this.shell, black, sx * R * 0.62, R * 0.0, R * 0.6, R * 0.32, R * 0.3, R * 0.33); // hand
      blob(this.shell, pad, sx * R * 0.62, -R * 0.03, R * 0.79, R * 0.13, R * 0.12, R * 0.07); // paw pad
    }
    for (const sx of [-1, 1]) {
      blob(this.shell, black, sx * R * 0.5, -R * 0.73, R * 0.34, R * 0.34, R * 0.3, R * 0.34); // foot
      blob(this.shell, pad, sx * R * 0.5, -R * 0.66, R * 0.56, R * 0.17, R * 0.14, R * 0.09); // sole
    }

    // Little tail at the back of the belly.
    blob(this.shell, white, 0, -R * 0.28, -R * 0.84, R * 0.18, R * 0.16, R * 0.16);

    // --- Face (built facing +Z, relative to the head centre) ---
    const eyePatch = new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.6 });
    const sclera = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.35 });
    const pupilMat = new THREE.MeshStandardMaterial({ color: 0x0c0c0c, roughness: 0.25 });
    const hiMat = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.2 });
    const noseMat = new THREE.MeshStandardMaterial({ color: 0x171717, roughness: 0.5 });

    const patchGeo = new THREE.SphereGeometry(R * 0.22, 16, 12);
    const eyeGeo = new THREE.SphereGeometry(R * 0.11, 16, 12);
    const pupilGeo = new THREE.SphereGeometry(R * 0.07, 14, 10);
    const hiGeo = new THREE.SphereGeometry(R * 0.027, 8, 8);

    this.basePupilX = R * 0.2;
    const mkEye = (sx: number) => {
      const ex = sx * R * 0.2;
      // Big black almond patch (the panda look).
      const patch = new THREE.Mesh(patchGeo, eyePatch);
      patch.scale.set(1, 1.3, 0.45);
      patch.position.set(ex, R * 0.04, R * 0.36);
      // Flattened white eye so it sits on the patch instead of bulging out.
      const eye = new THREE.Mesh(eyeGeo, sclera);
      eye.scale.set(1, 1, 0.55);
      eye.position.set(ex, R * 0.06, R * 0.46);
      // Clear dark pupil + a little highlight for life.
      const pupil = new THREE.Mesh(pupilGeo, pupilMat);
      pupil.position.set(ex, R * 0.05, R * 0.51);
      const hi = new THREE.Mesh(hiGeo, hiMat);
      hi.position.set(ex - sx * R * 0.022, R * 0.09, R * 0.54);
      this.face.add(patch, eye, pupil, hi);
      return { eye, pupil };
    };
    const l = mkEye(-1);
    const r = mkEye(1);
    this.eyeL = l.eye;
    this.eyeR = r.eye;
    this.pupilL = l.pupil;
    this.pupilR = r.pupil;

    // Nose + mouth: floated slightly proud of the curving head so they read
    // clearly. Centred just under the eyes.
    const nose = new THREE.Mesh(new THREE.SphereGeometry(R * 0.085, 14, 10), noseMat);
    nose.scale.set(1.5, 1.0, 0.85);
    nose.position.set(0, -R * 0.04, R * 0.56);
    this.face.add(nose);

    // Mouth: a smile by default (half-ring opening upward); a separate frown
    // (opening downward) is shown only when sad/dazed.
    const mouthTube = new THREE.TorusGeometry(R * 0.11, R * 0.024, 8, 18, Math.PI);
    this.mouth = new THREE.Mesh(mouthTube, noseMat);
    this.mouth.rotation.z = Math.PI; // flip the arc -> smile (∪)
    this.mouth.position.set(0, -R * 0.13, R * 0.55);
    this.frown = new THREE.Mesh(mouthTube, noseMat);
    this.frown.position.set(0, -R * 0.18, R * 0.55); // ∩
    this.frown.visible = false;
    this.face.add(this.mouth, this.frown);
  }

  setExpression(e: Expression): void {
    this.expression = e;
    if (e === 'dazed') this.dazeTime = 1.6;
    const sad = e === 'dazed';
    this.mouth.visible = !sad; // smile
    this.frown.visible = sad; // frown only when sad/dazed
    this.partyHat.visible = e === 'party';
  }

  /** Drive the tumbling body from the physics ball rotation (face leads forward). */
  setBodyRotation(q: THREE.Quaternion): void {
    this.shell.quaternion.copy(q).multiply(this.faceForward);
  }

  /** Trigger a squash impulse (e.g. on landing / bounce). */
  squashImpulse(amount: number): void {
    this.squash.set(1 + amount * 0.5, 1 - amount, 1 + amount * 0.5);
    this.squashVel.set(0, 0, 0);
  }

  update(dt: number, speed01: number): void {
    if (this.dazeTime > 0) this.dazeTime -= dt;

    // Expression.
    let pupilScale = 1;
    let mouthOpen = 0.35; // neutral: a calm, gentle mouth
    if (this.expression === 'scream') {
      pupilScale = 1.5;
      mouthOpen = 1.6;
    } else if (this.expression === 'content') {
      pupilScale = 1;
      mouthOpen = 0.95; // a nice full smile
    } else if (this.expression === 'party') {
      pupilScale = 1.25;
      mouthOpen = 1.35; // big excited grin
    }
    if (this.dazeTime > 0) {
      // Spinning daze: wobble the pupils.
      const w = Math.sin(this.dazeTime * 20) * 0.4;
      this.pupilL.position.x = -this.basePupilX + w * 0.1 * BALL_RADIUS;
      this.pupilR.position.x = this.basePupilX - w * 0.1 * BALL_RADIUS;
      pupilScale = 0.8;
    } else {
      // Settle the pupils back to centre.
      this.pupilL.position.x = -this.basePupilX;
      this.pupilR.position.x = this.basePupilX;
    }
    const ps = damp(this.pupilL.scale.x, pupilScale, 12, dt);
    this.pupilL.scale.setScalar(ps);
    this.pupilR.scale.setScalar(ps);
    const es = damp(this.eyeL.scale.y, 0.8 + speed01 * 0.6, 10, dt);
    this.eyeL.scale.y = es;
    this.eyeR.scale.y = es;
    this.mouth.scale.y = damp(this.mouth.scale.y, mouthOpen, 14, dt);

    // Spring the squash back to rest.
    for (const axis of ['x', 'y', 'z'] as const) {
      const k = 240;
      const c = 22;
      const accel = -k * (this.squash[axis] - 1) - c * this.squashVel[axis];
      this.squashVel[axis] += accel * dt;
      this.squash[axis] += this.squashVel[axis] * dt;
    }
    // Slight speed stretch (vertical squash) layered on top.
    const stretch = 1 + speed01 * 0.12;
    this.root.scale.set(this.squash.x / stretch, this.squash.y * stretch, this.squash.z / stretch);
  }
}
